import React, { useState } from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';

import { Loader } from 'ui';
import { CompanyHeader, CompanyBasicData, CompanyTabs } from 'ui/Company';
import { Button, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { theme } from 'theme';

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    margin: theme.spacing(2, 0),
    padding: theme.spacing(0, 2),
  },
  title: {
    paddingLeft: theme.spacing(3),
  },
  divider: {
    height: 4,
    background: theme.palette.grey[100],
  },
  yearSelectBoxContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    paddingBottom: theme.spacing(1.5),
  },
  yearButton: {
    maxWidth: theme.spacing(16),
    lineHeight: 1.2,
  },
}));

const CompanyPanel = ({
  loading,
  title,
  action,
  items,
  activeModule,
  onChangeModule,
  modules,
  availableYears,
  selectedYear,
  onYearChange,
  variant,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuClick = ({ currentTarget }) => {
    setMenuAnchorEl(currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (item) => {
    handleMenuClose();
    onYearChange(item);
    return null;
  };

  return (
    <Paper variant="elevation" elevation={3} className={classes.container}>
      {loading && <Loader cover />}
      <CompanyHeader title={title} action={action} />
      <Divider className={classes.divider} />
      <CompanyBasicData items={items} />
      <Divider className={classes.divider} />
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gridGap={theme.spacing(2)}
      >
        <CompanyTabs
          active={activeModule}
          onChange={onChangeModule}
          modules={modules}
          variant={variant}
        />
        {availableYears && availableYears.length > 0 && (
          <>
            <Button
              className={classes.yearButton}
              size="small"
              variant="outlined"
              onClick={
                availableYears.length === 1
                  ? () => handleMenuItemClick(availableYears[0])
                  : handleMenuClick
              }
              endIcon={
                availableYears.length > 1 &&
                (menuOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />)
              }
              disabled={availableYears.length === 1 ? availableYears[0].disabled : false}
            >
              {t(selectedYear ? `${selectedYear.year} Score overview` : 'Select year')}
            </Button>
            <Menu
              anchorEl={menuAnchorEl}
              getContentAnchorEl={null}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              open={menuOpen}
              onClose={handleMenuClose}
            >
              {availableYears.map((menuItem) => (
                <MenuItem
                  key={menuItem.latestUpdate}
                  onClick={() => handleMenuItemClick(menuItem)}
                  disabled={menuItem.disabled}
                >
                  {`${menuItem.year} Score overview`}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
    </Paper>
  );
};

CompanyPanel.defaultProps = {
  action: undefined,
  loading: false,
  availableYears: undefined,
  selectedYear: undefined,
  onYearChange: undefined,
};

CompanyPanel.propTypes = {
  loading: PropTypes.bool,
  title: PropTypes.string.isRequired,
  action: PropTypes.node,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      onClick: PropTypes.func,
    }),
  ).isRequired,
  activeModule: PropTypes.string.isRequired,
  onChangeModule: PropTypes.func.isRequired,
  modules: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  availableYears: PropTypes.array,
  selectedYear: PropTypes.object,
  onYearChange: PropTypes.func,
  variant: PropTypes.string,
};

export default CompanyPanel;
